/*
 * MappingDataGrid
 *
 * AgGrid component for displaying mapping data
 */

import { AgGridReact } from 'ag-grid-react';
import {
  RowSelectionOptions,
  GridOptions,
  CellClassParams,
  CellValueChangedEvent,
  CellClickedEvent,
} from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import { useSelector } from 'react-redux';
import { useRef, useEffect } from 'react';

import styled from 'styled-components';
import { useLatest } from '../../../hooks/useLatest';
import {
  SCREEN_MODES,
  TABS,
  COLUMN_ID,
  MAPPED_VALUES_COLUMN_IDS,
  COLUMN_IDS_EDIT_NOT_ALLOWED,
  SYSTEM_GENERATED_FIELD_NAMES,
  PRIMARY_KEY_FIELD_NAMES,
} from '../constants';
import { getTabColumns } from '../gridColumns';
import {
  clearSelectedRows,
  selectRows,
  updateDraftRow,
  clearValidationErrorForField,
  selectIsAnyLoading,
} from '../../../features/mappingAdmin/mappingAdminSlice';
import { MappingAdminState } from '../../../common/domain/mappingAdmin';
import { ActionCell } from './ActionCell';
import { FieldMappingDataRow } from '../../../common/domain/mappingAdmin';
import { GridApi } from 'ag-grid-community';
import { MappingDataGridProps } from '../interfaces';

const getDateWithOffset = (offsetYears: number = 0): string => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + offsetYears);
  return d.toISOString().slice(0, 19);
};

const isEmptyValue = (v: unknown): boolean =>
  v == null || (typeof v === 'string' && v.trim() === '');

const GridContainer = styled.div`
  height: 55vh;
  width: 100%;
  margin-top: 1rem;
`;

const CELL_STYLES = {
  edited: { backgroundColor: '#6bd098' },
  deleted: {
    backgroundColor: 'lightgray',
    textDecoration: 'line-through',
    color: 'gray',
  },
  disabled: {
    backgroundColor: '#f5f5f5',
    color: '#999',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  validationError: {
    border: '1px solid #ff4757',
  },
} as const;

const MappingDataGrid = ({
  rowData,
  screenMode,
  tabType,
  activeTab,
  username,
  dispatch,
  onGridReady: onGridReadyProp,
}: MappingDataGridProps) => {
  const {
    draftRow,
    addedRows,
    editedRows,
    deletedRows,
    validationErrors,
    rowEditInProgress,
    dataGroups,
    showUpdatedOnly,
    ratesData,
    mappingDataValues,
  } = useSelector((state: any) => state.mappingAdmin as MappingAdminState);
  const isAnyLoading = useSelector(selectIsAnyLoading);

  const gridApiRef = useRef<GridApi | null>(null);
  const draftRowRef = useLatest(draftRow);
  const rowEditInProgressRef = useLatest(rowEditInProgress);
  const showUpdatedOnlyRef = useLatest(showUpdatedOnly);
  const deletedRowsRef = useLatest(deletedRows);
  const addedRowsRef = useLatest(addedRows);
  const editedRowsRef = useLatest(editedRows);

  const isFieldUpdated = (rowId: string, fieldName: string): boolean => {
    const editedRow = editedRows[rowId];
    if (!editedRow) return false;
    const { oldData, newData } = editedRow;
    return (
      oldData[fieldName as keyof FieldMappingDataRow] !==
      newData[fieldName as keyof FieldMappingDataRow]
    );
  };

  const isRowBeingEdited = (rowId: string) =>
    rowEditInProgressRef.current && draftRowRef.current?.data.id === rowId;

  const isRowDisabled = (rowId: string) =>
    rowEditInProgressRef.current && draftRowRef.current?.data.id !== rowId;

  const getCellStyle = (params: CellClassParams) => {
    const rowId = params.data.id;
    const fieldName = params.column.getId();
    if (fieldName === COLUMN_ID.ACTION) {
      return {};
    }
    if (validationErrors[rowId]?.includes(fieldName)) {
      return CELL_STYLES.validationError;
    }
    if (
      isRowDisabled(rowId) ||
      (isRowBeingEdited(rowId) && !params.column.isCellEditable(params.data))
    ) {
      return CELL_STYLES.disabled;
    } else if (deletedRows[rowId]) {
      return CELL_STYLES.deleted;
    } else if (
      addedRows[rowId] ||
      (editedRows[rowId] && isFieldUpdated(rowId, fieldName))
    ) {
      return CELL_STYLES.edited;
    }
    return {};
  };

  const getDisabledReason = (params: any): string | null => {
    if (screenMode !== SCREEN_MODES.EDITING_MODE) return null;
    const rowId = params.node?.id;
    const tabRates = ratesData?.[tabType] ?? {};
    const isExistingRow = !!draftRow?.data?.id && draftRow.data.id in tabRates;
    const isThisRowBeingEdited = isRowBeingEdited(rowId);

    if (isThisRowBeingEdited && !isExistingRow) return null;

    const colId = params.column?.getId();
    if (SYSTEM_GENERATED_FIELD_NAMES.includes(colId)) {
      return 'This field is automatically generated and cannot be edited.';
    }
    if (PRIMARY_KEY_FIELD_NAMES.includes(colId)) {
      return 'This field is part of the primary key and cannot be edited.';
    }
    if (isRowDisabled(rowId)) return 'Another row is being edited';
    return null;
  };

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.onFilterChanged();
    }
  }, [showUpdatedOnly]);

  useEffect(() => {
    if (activeTab !== tabType) return;
    const api = gridApiRef.current;
    if (!api) return;
    const timer = requestAnimationFrame(() => {
      gridApiRef.current?.sizeColumnsToFit();
    });
    return () => cancelAnimationFrame(timer);
  }, [screenMode, tabType, activeTab]);

  useEffect(() => {
    if (
      rowEditInProgress &&
      draftRow?.tabType === tabType &&
      gridApiRef.current
    ) {
      const gridApi = gridApiRef.current;
      const targetNode = gridApi.getRowNode(draftRow.data.id);
      if (targetNode && targetNode.rowIndex != null) {
        requestAnimationFrame(() => {
          let columnToEdit = COLUMN_ID.GROUP_ID;
          if (draftRow.data.id in ratesData[tabType]) {
            if (tabType === TABS.RATES_FIELD) {
              columnToEdit = COLUMN_ID.MAPPED_VALUE;
            } else {
              columnToEdit = COLUMN_ID.MAPPED_REFERENCE_INDEX;
            }
          }
          gridApi.startEditingCell({
            rowIndex: targetNode.rowIndex!,
            colKey: columnToEdit,
          });
        });
      }
    }
  }, [
    rowEditInProgress,
    draftRow?.tabType,
    draftRow?.data.id,
    ratesData,
    tabType,
  ]);

  const onGridReady = (params: any) => {
    gridApiRef.current = params.api;
    requestAnimationFrame(() => {
      params.api.sizeColumnsToFit();
    });
    onGridReadyProp?.(params.api);
  };

  const syncSelectionToDisplayedOnly = (api: GridApi) => {
    const selectedNodes = api.getSelectedNodes();
    const nodesToSync = selectedNodes.filter((node: any) => node.displayed);
    dispatch(clearSelectedRows({ tabTypes: [tabType] }));
    dispatch(
      selectRows({
        rowsData: nodesToSync.map((node: any) => node.data),
        tabType,
      }),
    );
    if (selectedNodes.length !== nodesToSync.length) {
      api.deselectAll();
      nodesToSync.forEach((node: any) => node.setSelected(true));
    }
  };

  const onSelectionChanged = (params: any) => {
    syncSelectionToDisplayedOnly(params.api);
  };

  const onCellClicked = (params: CellClickedEvent) => {
    if (
      isRowBeingEdited(params.node.id!) &&
      params.column.getId() !== COLUMN_ID.ACTION &&
      params.api.getEditingCells().length === 0
    ) {
      params.api.startEditingCell({
        rowIndex: params.node.rowIndex!,
        colKey: params.column.getId(),
      });
    }
  };

  const onCellValueChanged = (params: CellValueChangedEvent) => {
    const colId = params.column.getId();
    if (colId === COLUMN_ID.FIELD_NAME) {
      const selectedFieldName = params.newValue;
      const previousFieldName = params.oldValue;
      if (previousFieldName !== selectedFieldName) {
        MAPPED_VALUES_COLUMN_IDS.forEach((colId) => {
          params.node.setDataValue(colId, null);
        });
      }
    }
    dispatch(
      clearValidationErrorForField({
        rowId: params.node.id!,
        field: params.column.getId(),
      }),
    );
  };

  const doesExternalFilterPass = (node: any) => {
    if (!showUpdatedOnlyRef.current) return true;
    const rowId = node.data.id;

    return (
      !!addedRowsRef.current[rowId] ||
      !!editedRowsRef.current[rowId] ||
      !!deletedRowsRef.current[rowId] ||
      draftRowRef.current?.data.id === rowId
    );
  };

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    if (screenMode === SCREEN_MODES.SELECTING_MODE) {
      api.setGridOption('rowSelection', {
        mode: 'multiRow',
        enableClickSelection: false,
        selectAll: 'filtered',
      });
      const onFilterChanged = () => {
        setTimeout(() => {
          const currentApi = gridApiRef.current;
          if (currentApi) syncSelectionToDisplayedOnly(currentApi);
        }, 0);
      };
      api.addEventListener('filterChanged', onFilterChanged);
      return () => {
        api.removeEventListener('filterChanged', onFilterChanged);
      };
    } else {
      api.deselectAll();
      api.setGridOption('rowSelection', undefined);
    }
  }, [screenMode]);

  const tabSpecificColumns = getTabColumns(mappingDataValues, dataGroups)[
    tabType
  ]();

  const columnDefs: any[] = [
    {
      headerName: 'Action',
      field: COLUMN_ID.ACTION,
      cellRenderer: (params: any) => ActionCell({ params, username, dispatch }),
      width: 100,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: 'left',
      editable: false,
      hide: screenMode !== SCREEN_MODES.EDITING_MODE,
    },
    ...tabSpecificColumns,
  ].map((colDef) => ({
    ...colDef,
    cellStyle: getCellStyle,
  }));

  const isEditMode = screenMode === SCREEN_MODES.EDITING_MODE;

  const onCellEditingStarted = (params: any) => {
    if (screenMode !== SCREEN_MODES.EDITING_MODE) return;

    const colId = params.column?.getId();
    const current = params.data?.[colId];
    if (!isEmptyValue(current)) return;
    let defaultVal: string | null = null;

    if (colId === COLUMN_ID.VALID_FROM) {
      defaultVal = getDateWithOffset(0);
    } else if (colId === COLUMN_ID.VALID_TO) {
      defaultVal = getDateWithOffset(100);
    }
    if (defaultVal == null) return;
    params.node.setDataValue(colId, defaultVal);
    dispatch(updateDraftRow({ field: colId, value: defaultVal }));
    // Re-open the editor so it displays the default (editor was created with null)
    params.api.stopEditing(false);
    setTimeout(() => {
      params.api.startEditingCell({
        rowIndex: params.node.rowIndex!,
        colKey: colId,
      });
    }, 0);
  };

  const gridOptions: GridOptions = {
    animateRows: true,
    accentedSort: true,
    suppressClickEdit: true,
    onSelectionChanged: onSelectionChanged,
    onGridReady: onGridReady,
    onCellClicked: onCellClicked,
    onCellValueChanged: onCellValueChanged,
    onCellEditingStarted,
    getRowId: (params: any) => params.data.id,
    isExternalFilterPresent: () => showUpdatedOnlyRef.current,
    doesExternalFilterPass: doesExternalFilterPass,
    ...(isEditMode && {
      suppressModelUpdateAfterUpdateTransaction: true,
    }),
    tooltipShowDelay: 0,
  };

  if (screenMode === SCREEN_MODES.SELECTING_MODE) {
    gridOptions.rowSelection = {
      mode: 'multiRow',
      enableClickSelection: false,
      selectAll: 'filtered',
    } as RowSelectionOptions;
  }

  const isCellEditable = (params: any): boolean => {
    if (!isRowBeingEdited(params.node.id)) return false;
    if (params.column.getId() === COLUMN_ID.ACTION) return false;

    const tabRates = ratesData?.[tabType] ?? {};
    const draftId = draftRow?.data?.id;
    const isExistingRow = !!draftId && draftId in tabRates;

    if (!isExistingRow) return true;

    return !COLUMN_IDS_EDIT_NOT_ALLOWED.includes(params.column.getId());
  };

  return (
    <GridContainer className="ag-theme-alpine">
      <AgGridReact
        key={tabType}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={{
          resizable: true,
          sortable: !isEditMode,
          filter: !isEditMode,
          filterParams: {
            comparator: (a: string, b: string) =>
              a.localeCompare(b, undefined, { sensitivity: 'base' }),
          },
          tooltipValueGetter: (params: any) =>
            screenMode === SCREEN_MODES.EDITING_MODE
              ? getDisabledReason(params)
              : null,
          editable: isCellEditable,
          valueSetter: ({ colDef, newValue }) => {
            const field = colDef.field as string;
            let valueToSet = newValue;
            if (field === COLUMN_ID.VALID_FROM && isEmptyValue(newValue)) {
              valueToSet = getDateWithOffset(0);
            } else if (field === COLUMN_ID.VALID_TO && isEmptyValue(newValue)) {
              valueToSet = getDateWithOffset(100);
            }
            dispatch(
              updateDraftRow({
                field,
                value: valueToSet,
              }),
            );
            return true;
          },
        }}
        loading={isAnyLoading}
        gridOptions={gridOptions}
        modules={[AllCommunityModule]}
      />
    </GridContainer>
  );
};

export default MappingDataGrid;
